import { matchPath } from "react-router";

import { type IntegrationUserProfile } from "helpers/AppProvider.types";

import { type AnalyticsBuilderTraits } from "utilities/analytics/analytics.api.types";
import { routes } from "utilities/routes";

export const isPermittedForUnauthenticatedPageViewEvent = (url: string) => {
    // Read the route constants lazily inside the function: this module is imported through a
    // cycle (BTButton -> analytics -> mixpanelOverrides -> routes), so reading routes at module
    // load time can see an uninitialized routes object.
    const unauthenticatedSubPaymentRoutes = [
        routes.outboundPayments.external,
        routes.outboundPayments.setup,
    ];
    // properties.url may be a relative path or an absolute href, and may carry a query/hash;
    // matchPath wants a pathname, so strip everything from the first ? or # and let the leading
    // ** wildcard absorb any origin/prefix.
    const pathname = url.split(/[?#]/)[0];
    return (
        url.includes("/app/Media/Shared/") ||
        url.includes(routes.specification.external) ||
        url.includes("/Plans/External/") ||
        unauthenticatedSubPaymentRoutes.some(
            (route) =>
                matchPath(pathname, { path: `**${route}`, exact: false, strict: false }) != null,
        )
    );
};

function identify(params: any) {
    const { userId } = params.payload;
    const builderTraits: AnalyticsBuilderTraits = params.payload.traits.builderTraits;
    const integrationUserProfile: IntegrationUserProfile =
        params.payload.traits.integrationUserProfile;
    // eslint-disable-next-line no-restricted-syntax
    const mixpanel = (window as any).mixpanel;

    if (typeof userId === "string") {
        mixpanel.register({ builderId: builderTraits?.builderId });
        mixpanel.identify(userId);
    }
    if (builderTraits) {
        const mpTraits = {
            builder_name: builderTraits.builderName,
            builderId: builderTraits.builderId,
            flags: builderTraits.flags,
            canEditFiles: integrationUserProfile.canEditFiles,
        };
        mixpanel.people.set(mpTraits);
        try {
            mixpanel.get_group("builderId", builderTraits.builderId.toString()).set(mpTraits);
        } catch (e) {
            // Tracking blocker extensions cause this method to fail because the mixpanel source script fails to load.
            // Swallow them to prevent Sentry errors from being generated
            if (!(e instanceof ReferenceError)) {
                throw e;
            }
        }
    }
}

// When overriding this, we want to use the same track() call as the standard plugin
function trackEvent(trackFunc: any) {
    return function page(params: any) {
        const { userId } = params.payload;
        if (userId != null) {
            trackFunc(params);
        }
    };
}

// When overriding this, we want to use the same page() call as the standard plugin
function pageEvent(pageFunc: any) {
    return function page(params: any) {
        const { properties, userId } = params.payload;
        if (userId != null || isPermittedForUnauthenticatedPageViewEvent(properties.url as string))
            pageFunc(params);
    };
}

export function getMixpanelOverrides(plugin: any) {
    return {
        identify: identify,
        track: trackEvent(plugin.track),
        page: pageEvent(plugin.page),
    };
}
